<template>
  <div class="grid">
    <div class="col-12">
      <Card>
        <template #title>История событий по займам</template>
        <template #content>
          <div class="flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
            <div class="field mb-0" style="min-width: 320px; max-width: 520px; flex: 1">
              <label class="block mb-2"><strong>Типы событий</strong></label>
              <MultiSelect
                v-model="eventTypes"
                :options="eventTypeOptions"
                optionValue="code"
                optionLabel="value"
                placeholder="Выберите типы событий"
                display="chip"
                :filter="true"
                class="w-full"
                @change="onFilterChange"
              />
            </div>
            <div class="flex align-items-center gap-2">
              <Button
                v-if="selected.length"
                icon="pi pi-replay"
                :label="`Перевыгрузить выбранные (${selected.length})`"
                class="p-button-primary"
                @click="resendDialogVisible = true"
              />
              <Button
                v-if="hasActiveFilters"
                icon="pi pi-filter-slash"
                label="Сбросить фильтры"
                class="p-button-outlined p-button-secondary"
                @click="clearFilters"
              />
              <Button
                icon="pi pi-refresh"
                class="p-button-rounded p-button-outlined"
                @click="load"
              />
            </div>
          </div>

          <DataTable
            :value="items"
            :total-records="total"
            :rows="limit"
            :first="offset"
            :paginator="true"
            :lazy="true"
            :loading="loading"
            v-model:selection="selected"
            v-model:expanded-rows="expandedRows"
            data-key="id"
            responsive-layout="scroll"
            :show-gridlines="true"
            :rows-per-page-options="[10, 25, 50, 100]"
            current-page-report-template="{first} - {last} из {totalRecords}"
            paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            @page="onPage"
            @row-expand="onRowExpand"
          >
            <template #empty>
              <div style="text-align: center">
                <span v-if="!loading && eventTypes.length === 0">Выберите хотя бы один тип события</span>
                <span v-else-if="!loading">По выбранным типам событий записей нет</span>
              </div>
            </template>

            <Column selection-mode="multiple" header-style="width: 3em"></Column>
            <Column :expander="true" header-style="width: 3em"></Column>
            <Column field="cbUuid">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>ЦБ УИД</span>
                  <InputText v-model="filters.cbUuid" placeholder="Поиск..." class="p-inputtext-sm w-full" @input="onFilterInput" />
                </div>
              </template>
              <template #body="{ data }">
                <ValueChip
                  v-if="data.cbUuid"
                  :value="data.cbUuid"
                  toast-template='ЦБ УИД "{v}" скопирован'
                />
                <span v-else>—</span>
              </template>
            </Column>
            <Column field="loanId">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>ID займа</span>
                  <InputText v-model="filters.loanId" placeholder="ID..." class="p-inputtext-sm w-full" @input="onFilterInput" />
                </div>
              </template>
              <template #body="{ data }">
                <IdChip
                  :id="data.loanId"
                  view="loan_view"
                  :view-params="{ id: data.loanId }"
                  toast-template='ID займа "{v}" скопирован'
                />
              </template>
            </Column>
            <Column field="eventType">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>Тип события</span>
                </div>
              </template>
              <template #body="{ data }">
                {{ formatEventType(data) }}
              </template>
            </Column>
            <Column field="createdAt">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>Создано</span>
                  <input type="date" v-model="filters.createdAt" class="p-inputtext p-inputtext-sm w-full" @change="onFilterChange" />
                </div>
              </template>
            </Column>
            <Column field="updatedAt">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>Обновлено</span>
                  <input type="date" v-model="filters.updatedAt" class="p-inputtext p-inputtext-sm w-full" @change="onFilterChange" />
                </div>
              </template>
              <template #body="{ data }">
                {{ data.updatedAt || '—' }}
              </template>
            </Column>
            <Column field="correctionReportId">
              <template #header>
                <div class="flex flex-column w-full gap-1">
                  <span>Корректировка</span>
                  <InputText v-model="filters.correctionReportId" placeholder="№..." class="p-inputtext-sm w-full" @input="onFilterInput" />
                </div>
              </template>
              <template #body="{ data }">
                <a
                  v-if="data.correctionReportId"
                  :href="`/functions/resend-loan/${data.correctionReportId}`"
                  target="_blank"
                >№{{ data.correctionReportId }}</a>
                <span v-else>—</span>
              </template>
            </Column>
            <Column header="Действия" header-style="width: 10em">
              <template #body="{ data }">
                <Button
                  icon="pi pi-history"
                  label="История"
                  class="p-button-sm p-button-outlined"
                  @click="openHistory(data)"
                />
              </template>
            </Column>

            <template #expansion="{ data }">
              <div class="p-3">
                <strong>Последнее запись в истории:</strong>
                <div v-if="latestLoading[data.loanId]" class="mt-2">
                  <ProgressSpinner style="width: 24px; height: 24px"></ProgressSpinner>
                </div>
                <div v-else-if="latestCache[data.loanId] === null" class="mt-2 p-text-secondary">
                  Событий нет.
                </div>
                <div v-else-if="latestCache[data.loanId]" class="mt-2">
                  <div><strong>Тип:</strong> {{ formatEventType(latestCache[data.loanId]) }}</div>
                  <div><strong>Создано:</strong> {{ latestCache[data.loanId].createdAt }}</div>
                  <div v-if="latestCache[data.loanId].updatedAt"><strong>Обновлено:</strong> {{ latestCache[data.loanId].updatedAt }}</div>
                  <div v-if="latestCache[data.loanId].correctionReportId">
                    <strong>Корректировка:</strong>
                    <a :href="`/functions/resend-loan/${latestCache[data.loanId].correctionReportId}`" target="_blank">
                      №{{ latestCache[data.loanId].correctionReportId }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </DataTable>
        </template>
      </Card>
    </div>

    <LoanHistoryResendDialog
      v-model:visible="resendDialogVisible"
      :selected="selected"
      @done="onResendDone"
    />
    <LoanHistoryEventsDialog
      v-model:visible="historyDialogVisible"
      :loan-id="historyForLoan"
    />
  </div>
</template>

<script>
import MultiSelect from "primevue/multiselect";
import InputText from "primevue/inputtext";
import LoanHistoryService from "@/service/LoanHistoryService";
import ResponseService from "@/service/ResponseService";
import IdChip from "@/components/Common/IdChip.vue";
import ValueChip from "@/components/Common/ValueChip.vue";
import LoanHistoryResendDialog from "@/components/Dialog/LoanHistoryResendDialog.vue";
import LoanHistoryEventsDialog from "@/components/Dialog/LoanHistoryEventsDialog.vue";

export default {
  name: "LoanHistory",
  components: { MultiSelect, InputText, IdChip, ValueChip, LoanHistoryResendDialog, LoanHistoryEventsDialog },
  data() {
    return {
      eventTypes: ['cancel_close'],
      filters: {
        cbUuid: '',
        loanId: '',
        correctionReportId: '',
        createdAt: '',
        updatedAt: '',
      },
      filterTimeout: null,
      items: [],
      total: 0,
      loading: false,
      limit: 50,
      offset: 0,
      selected: [],
      expandedRows: [],
      latestCache: {},
      latestLoading: {},
      resendDialogVisible: false,
      historyDialogVisible: false,
      historyForLoan: null,
    }
  },
  computed: {
    eventTypeOptions() {
      return this.$store.state.compendiums.loanHistoryEventType || [];
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(val => val !== '' && val !== null && val !== undefined);
    }
  },
  methods: {
    buildFilterParams() {
      const p = {};
      if (this.filters.cbUuid && this.filters.cbUuid.trim()) {
        p.cbUuid = this.filters.cbUuid.trim();
      }
      if (this.filters.loanId && String(this.filters.loanId).trim()) {
        const cleanLoanId = String(this.filters.loanId).replace(/\D+/g, '');
        if (cleanLoanId) {
          p.loanId = cleanLoanId;
        }
      }
      if (this.filters.correctionReportId && String(this.filters.correctionReportId).trim()) {
        const cleanReportId = String(this.filters.correctionReportId).replace(/\D+/g, '');
        if (cleanReportId) {
          p.correctionReportId = cleanReportId;
        }
      }
      if (this.filters.createdAt) {
        p.createdAt = this.filters.createdAt;
      }
      if (this.filters.updatedAt) {
        p.updatedAt = this.filters.updatedAt;
      }
      return p;
    },
    load() {
      if (this.eventTypes.length === 0) {
        this.items = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      const filterParams = this.buildFilterParams();
      LoanHistoryService.byEventTypes(this.eventTypes, this.limit, this.offset, filterParams).then((res) => {
        this.items = res.items || [];
        this.total = res.total || 0;
      }).catch((err) => {
        this.items = [];
        this.total = 0;
        ResponseService.handleErrorResponseWithoutForm(err, this.$toast);
      }).finally(() => {
        this.loading = false;
      });
    },
    onFilterInput() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.onFilterChange();
      }, 350);
    },
    onFilterChange() {
      clearTimeout(this.filterTimeout);
      this.offset = 0;
      this.selected = [];
      this.load();
    },
    clearFilters() {
      clearTimeout(this.filterTimeout);
      this.filters = {
        cbUuid: '',
        loanId: '',
        correctionReportId: '',
        createdAt: '',
        updatedAt: '',
      };
      this.onFilterChange();
    },
    formatEventType(data) {
      if (!data) return '';
      const name = this.$filters.translate(this.$store, 'loanHistoryEventType', data.eventType);
      if (data.eventType === 'auto_delete' && data.deleteCode) {
        return `${name} (${data.deleteCode})`;
      }
      return name;
    },
    onPage(event) {
      this.offset = event.first;
      this.limit = event.rows;
      this.load();
    },
    onRowExpand(event) {
      const loanId = event.data.loanId;
      if (this.latestCache[loanId] !== undefined || this.latestLoading[loanId]) {
        return;
      }
      this.latestLoading[loanId] = true;
      LoanHistoryService.latest({ loanId }).then((res) => {
        this.latestCache[loanId] = res.item || null;
      }).catch((err) => {
        ResponseService.handleErrorResponseWithoutForm(err, this.$toast);
      }).finally(() => {
        this.latestLoading[loanId] = false;
      });
    },
    openHistory(row) {
      this.historyForLoan = row.loanId;
      this.historyDialogVisible = true;
    },
    onResendDone() {
      this.selected = [];
      this.load();
    }
  },
  mounted() {
    this.load();
  }
}
</script>
